import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faUserPlus, faEye, faTrash, faSearch, faSort, faSortUp, faSortDown, faEdit, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getAllConsultants, deleteConsultant, updateConsultant } from '../../services/api';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import type { Column } from 'react-table';

interface Consultant {
  id: string;
  name: string;
  email: string;
  created_at: string;
  client_count: number;
}

const ConsultantsList: React.FC = () => {
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in and is a consultant
    const token = localStorage.getItem('auth_token');
    const userRole = localStorage.getItem('user_role');
    
    if (!token || userRole !== 'super') {
      console.log('[AUTH] No valid consultant token found, redirecting to login');
      navigate('/login');
      return;
    }
    
    fetchConsultants();
  }, [navigate]);

  const fetchConsultants = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllConsultants();
      setConsultants(response.consultants || response);
    } catch (error: any) {
      console.error('Error fetching consultants:', error);
      setError(error.message || 'Failed to load consultants');
      toast.error(error.message || 'Failed to load consultants');
    } finally {
      setLoading(false);
    }
  };

  // Define columns for react-table
  const columns = useMemo<Column<Consultant>[]>(() => [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: any) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
            {row.original.name.charAt(0).toUpperCase()}
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
          </div>
        </div>
      ),
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ value }: any) => (
        <div className="text-sm text-gray-500">{value}</div>
      ),
    },
    {
      Header: 'Joined',
      accessor: 'created_at',
      Cell: ({ value }: any) => (
        <div className="text-sm text-gray-500">{formatDate(value)}</div>
      ),
    },
    {
      Header: 'Clients',
      accessor: 'client_count',
      Cell: ({ value }: any) => (
        <div className="text-sm text-gray-500">
          {value} {value === 1 ? 'client' : 'clients'}
        </div>
      ),
    },
    {
      Header: 'Actions',
      Cell: ({ row }: any) => {
        const consultant = row.original;
        return (
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => navigate(`/consultant/consultants/${consultant.id}`)}
              className="text-blue-600 hover:text-blue-900"
              title="View details"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
            <button
              onClick={() => openEditModal(consultant)}
              className="text-green-600 hover:text-green-900"
              title="Edit consultant"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={() => openDeleteModal(consultant)}
              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={consultant.client_count > 0}
              title={consultant.client_count > 0 ? "Cannot delete consultant with active clients" : "Delete consultant"}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        );
      },
      disableSortBy: true,
    },
  ], [navigate]);

  // Custom global filter function for consultants
  const globalFilter = useMemo(() => {
    return (rows: any[], columnIds: string[], filterValue: string) => {
      if (!filterValue) return rows;
      
      const searchLower = filterValue.toLowerCase();
      return rows.filter(row => {
        // Search in name and email
        const name = row.original.name?.toLowerCase() || '';
        const email = row.original.email?.toLowerCase() || '';
        
        return name.includes(searchLower) || email.includes(searchLower);
      });
    };
  }, []);

  // Table instance
  const tableInstance = useTable(
    {
      columns,
      data: consultants,
      initialState: { pageIndex: 0, pageSize: 10 } as any,
      globalFilter,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  ) as any;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter: globalFilterValue },
    setGlobalFilter,
    prepareRow,
  } = tableInstance;

  const openDeleteModal = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedConsultant(null);
  };

  const handleDeleteConsultant = async () => {
    if (!selectedConsultant) return;
    
    try {
      await deleteConsultant(selectedConsultant.id);
      toast.success('Consultant deleted successfully');
      setConsultants(consultants.filter(c => c.id !== selectedConsultant.id));
      closeDeleteModal();
    } catch (error: any) {
      console.error('Error deleting consultant:', error);
      toast.error(error.message || 'Failed to delete consultant');
      closeDeleteModal();
    }
  };

  const openEditModal = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setEditFormData({
      name: consultant.name,
      email: consultant.email,
      password: '',
      confirmPassword: ''
    });
    setEditErrors({});
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedConsultant(null);
    setEditFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
    setEditErrors({});
  };

  const validateEditForm = () => {
    const errors: Record<string, string> = {};

    if (!editFormData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!editFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
      errors.email = 'Invalid email format';
    }

    if (editFormData.password) {
      if (editFormData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (editFormData.password !== editFormData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEditForm() || !selectedConsultant) {
      return;
    }

    setSaving(true);
    try {
      const updateData: any = {
        name: editFormData.name,
        email: editFormData.email
      };

      // Only include password if it's been changed
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      await updateConsultant(selectedConsultant.id, updateData);
      
      // Update the consultant in the list
      setConsultants(consultants.map(c => 
        c.id === selectedConsultant.id 
          ? { ...c, name: editFormData.name, email: editFormData.email }
          : c
      ));
      
      toast.success('Consultant updated successfully');
      closeEditModal();
    } catch (error: any) {
      console.error('Error updating consultant:', error);
      toast.error(error.message || 'Failed to update consultant');
    } finally {
      setSaving(false);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (editErrors[name]) {
      setEditErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Consultants Management</h1>
        <Link 
          to="/consultant/consultants/new" 
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
          <span>Add New Consultant</span>
        </Link>
      </div>
      
      <div className="bg-white rounded-lg shadow-md">
        <div className="relative">
          <style>{`
            .table-container::-webkit-scrollbar {
                height: 12px;
            }
            .table-container::-webkit-scrollbar:vertical {
                width: 0;
            }
            .table-container::-webkit-scrollbar-track {
                background: #E5E7EB;
                border-radius: 6px;
            }
            .table-container::-webkit-scrollbar-thumb {
                background: #065BDF;
                border-radius: 6px;
            }
            .table-container::-webkit-scrollbar-thumb:hover {
                background: #0547B3;
            }
          `}</style>
          {/* Sticky Header with Search and Controls */}
          <div className="sticky top-0 z-20 bg-white border-b border-gray-200 p-6">
            <div className="flex justify-between items-center">
              <div className="relative w-full md:w-96">
                <input
                  type="text"
                  value={globalFilterValue || ''}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      const table = document.querySelector('.table-container');
                      if (table) table.scrollBy({ left: -300, behavior: 'smooth' });
                    }}
                    className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                    title="Scroll left"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Scroll</div>
                  <button
                    onClick={() => {
                      const table = document.querySelector('.table-container');
                      if (table) table.scrollBy({ left: 300, behavior: 'smooth' });
                    }}
                    className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                    title="Scroll right"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        
          <div className="p-6">
          {loading ? (
          <div className="flex justify-center items-center py-16">
            <FontAwesomeIcon icon={faSync} spin className="text-blue-500 mr-2" size="lg" />
            <p className="text-gray-600">Loading consultants...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={fetchConsultants}
                  className="mt-2 text-sm text-red-700 hover:text-red-600 font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto table-container" style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#065BDF #E5E7EB'
            }}>
              <table {...getTableProps()} className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {headerGroups.map((headerGroup: any) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column: any) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center">
                            {column.render('Header')}
                            {column.canSort && (
                              <span className="ml-2">
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <FontAwesomeIcon icon={faSortDown} className="text-blue-600" />
                                  ) : (
                                    <FontAwesomeIcon icon={faSortUp} className="text-blue-600" />
                                  )
                                ) : (
                                  <FontAwesomeIcon icon={faSort} className="text-gray-400" />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                  {page.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-16 text-center text-gray-500">
                        No consultants found.
                        {globalFilterValue && (
                          <div className="mt-2">
                            <button
                              onClick={() => setGlobalFilter('')}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Clear search
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    page.map((row: any) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} className="hover:bg-gray-50">
                          {row.cells.map((cell: any) => (
                            <td {...cell.getCellProps()} className="px-6 py-4 whitespace-nowrap">
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="sticky bottom-0 bg-white px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  First
                </button>
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
                <button
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Last
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  Page{' '}
                  <strong>
                    {pageIndex + 1} of {pageOptions.length || 1}
                  </strong>
                </span>
                <span className="text-sm text-gray-700">| Go to page:</span>
                <input
                  type="number"
                  value={pageIndex + 1}
                  onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    gotoPage(page);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-16"
                />
              </div>
            </div>
          </>
        )}
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedConsultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Consultant</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete consultant <span className="font-semibold">{selectedConsultant.name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConsultant}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Consultant Modal */}
      {editModalOpen && selectedConsultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Consultant</h3>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Name Field */}
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    editErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter consultant name"
                />
                {editErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{editErrors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    editErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {editErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{editErrors.email}</p>
                )}
              </div>

              {/* Password Section */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Change Password (Optional)</h4>
                
                {/* New Password */}
                <div className="mb-3">
                  <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="edit-password"
                    name="password"
                    value={editFormData.password}
                    onChange={handleEditInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      editErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Leave blank to keep current password"
                  />
                  {editErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{editErrors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="edit-confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="edit-confirmPassword"
                    name="confirmPassword"
                    value={editFormData.confirmPassword}
                    onChange={handleEditInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      editErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm new password"
                  />
                  {editErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{editErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantsList; 